import React, { useState } from 'react';
import emailjs from 'emailjs-com';
import './booking.css';

function Bookings() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  const [bookingForm, setBookingForm] = useState({
    vehicleType: '',
    pickupLocation: '',
    destination: '',
    date: '',
    time: '',
    passengers: 1,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    specialRequests: ''
  });

  const vehicleTypes = [
    { id: 'sedan', name: 'Sedan', capacity: 'Up to 4 passengers' },
    { id: 'suv', name: 'SUV', capacity: 'Up to 6 passengers' },
    { id: 'luxury', name: 'Luxury Car', capacity: 'Up to 4 passengers' },
    { id: 'van', name: 'Van', capacity: 'Up to 8 passengers' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingForm({
      ...bookingForm,
      [name]: value
    });
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Final validation before submission
    if (!bookingForm.vehicleType) {
      setIsSubmitting(false);
      setSubmitStatus('error');
      return;
    }

    try {
      const selectedVehicle = vehicleTypes.find(v => v.id === bookingForm.vehicleType);
      
      const templateParams = {
        vehicle_type: selectedVehicle ? selectedVehicle.name : '',
        pickup_location: bookingForm.pickupLocation,
        destination: bookingForm.destination,
        date: bookingForm.date,
        time: bookingForm.time,
        passengers: bookingForm.passengers,
        customer_name: bookingForm.customerName,
        customer_email: bookingForm.customerEmail,
        customer_phone: bookingForm.customerPhone,
        special_requests: bookingForm.specialRequests || 'None',
        to_email: 'info@ecorichtravels.com',
        booking_time: new Date().toLocaleString()
      };

      await emailjs.send(
        'service_hyvom0x',
        'template_0m2cp7r',
        templateParams,
        'lM41GBFi-wpxugewO'
      );

      setIsSubmitting(false);
      setSubmitStatus('success');
      
      setTimeout(() => {
        setBookingForm({
          vehicleType: '',
          pickupLocation: '',
          destination: '',
          date: '',
          time: '',
          passengers: 1,
          customerName: '',
          customerEmail: '',
          customerPhone: '',
          specialRequests: ''
        });
        setCurrentStep(1);
      }, 3000);

    } catch (error) {
      console.error('Booking failed:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  };

  // Check if current step is valid to proceed
  const isStepValid = () => {
    switch(currentStep) {
      case 1:
        return bookingForm.customerName && bookingForm.customerEmail && bookingForm.customerPhone;
      case 2:
        return bookingForm.pickupLocation && bookingForm.destination && bookingForm.date && bookingForm.time;
      case 3:
        return bookingForm.vehicleType;
      default:
        return false;
    }
  };

  return (
    <div className="booking-page">
      {/* Header */}
      <section className="booking-header">
        <div className="container">
          <div className="header-content">
            <h1 className="header-title">Book Your Ride</h1>
            <p className="header-subtitle">Fast, reliable, and comfortable transportation</p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="booking-form-section">
        <div className="container">
          <div className="booking-form-container">
            
            {/* Progress Bar */}
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
                ></div>
              </div>
              <div className="progress-steps">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="progress-step">
                    <div className={`step-number ${currentStep >= step ? 'active' : ''}`}>
                      {step}
                    </div>
                    <span className="step-label">
                      {step === 1 && 'Personal Info'}
                      {step === 2 && 'Trip Details'}
                      {step === 3 && 'Vehicle & Book'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Success/Error Messages */}
            {submitStatus === 'success' && (
              <div className="booking-success">
                <div className="success-content">
                  <h3>✅ Booking Confirmed!</h3>
                  <p>Thank you for your booking! We'll contact you shortly.</p>
                  <div className="booking-summary">
                    <p><strong>Vehicle:</strong> {vehicleTypes.find(v => v.id === bookingForm.vehicleType)?.name}</p>
                    <p><strong>Pickup:</strong> {bookingForm.pickupLocation}</p>
                    <p><strong>Destination:</strong> {bookingForm.destination}</p>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="booking-error">
                <div className="error-content">
                  <h3>❌ Booking Failed</h3>
                  <p>Please try again or contact us directly.</p>
                  <button 
                    className="retry-btn"
                    onClick={() => setSubmitStatus(null)}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {/* Main Booking Form */}
            {!submitStatus && (
              <form className="booking-form" onSubmit={handleSubmitBooking}>
                <div className={`form-step ${currentStep === 1 ? 'active' : ''}`}>
                  {/* Step 1: Personal Information */}
                  <div className="form-content">
                    <h3 className="form-section-title">Personal Information</h3>
                    
                    <div className="form-group">
                      <label htmlFor="customerName">Full Name *</label>
                      <input
                        type="text"
                        id="customerName"
                        name="customerName"
                        value={bookingForm.customerName}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="customerEmail">Email *</label>
                      <input
                        type="email"
                        id="customerEmail"
                        name="customerEmail"
                        value={bookingForm.customerEmail}
                        onChange={handleInputChange}
                        placeholder="Enter your email"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="customerPhone">Phone Number *</label>
                      <input
                        type="tel"
                        id="customerPhone"
                        name="customerPhone"
                        value={bookingForm.customerPhone}
                        onChange={handleInputChange}
                        placeholder="Enter your phone number"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className={`form-step ${currentStep === 2 ? 'active' : ''}`}>
                  {/* Step 2: Trip Details */}
                  <div className="form-content">
                    <h3 className="form-section-title">Trip Details</h3>
                    
                    <div className="form-group">
                      <label htmlFor="pickupLocation">Pickup Location *</label>
                      <input
                        type="text"
                        id="pickupLocation"
                        name="pickupLocation"
                        value={bookingForm.pickupLocation}
                        onChange={handleInputChange}
                        placeholder="Enter pickup address"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="destination">Destination *</label>
                      <input
                        type="text"
                        id="destination"
                        name="destination"
                        value={bookingForm.destination}
                        onChange={handleInputChange}
                        placeholder="Enter destination"
                        required
                      />
                    </div>

                    <div className="form-row-sm">
                      <div className="form-group">
                        <label htmlFor="date">Date *</label>
                        <input
                          type="date"
                          id="date"
                          name="date"
                          value={bookingForm.date}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="time">Time *</label>
                        <input
                          type="time"
                          id="time"
                          name="time"
                          value={bookingForm.time}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="passengers">Number of Passengers</label>
                      <select
                        id="passengers"
                        name="passengers"
                        value={bookingForm.passengers}
                        onChange={handleInputChange}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                          <option key={num} value={num}>{num} {num === 1 ? 'passenger' : 'passengers'}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className={`form-step ${currentStep === 3 ? 'active' : ''}`}>
                  {/* Step 3: Vehicle Selection & Special Requests */}
                  <div className="form-content">
                    <h3 className="form-section-title">Vehicle & Requests</h3>
                    
                    <div className="form-group">
                      <label htmlFor="vehicleType">Select Vehicle Type *</label>
                      <select
                        id="vehicleType"
                        name="vehicleType"
                        value={bookingForm.vehicleType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Choose a vehicle type</option>
                        {vehicleTypes.map(vehicle => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.name} - {vehicle.capacity}
                          </option>
                        ))}
                      </select>
                      {currentStep === 3 && !bookingForm.vehicleType && (
                        <div className="validation-error">Please select a vehicle type</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="specialRequests">Special Requests</label>
                      <textarea
                        id="specialRequests"
                        name="specialRequests"
                        value={bookingForm.specialRequests}
                        onChange={handleInputChange}
                        placeholder="Any special requirements? (child seats, extra luggage, etc.)"
                        rows="4"
                      />
                    </div>

                    {/* Booking Summary Preview */}
                    <div className="booking-preview">
                      <h4>Booking Summary</h4>
                      <div className="preview-details">
                        <div className="preview-row">
                          <span>Vehicle:</span>
                          <strong>{vehicleTypes.find(v => v.id === bookingForm.vehicleType)?.name || 'Not selected'}</strong>
                        </div>
                        <div className="preview-row">
                          <span>Passengers:</span>
                          <strong>{bookingForm.passengers}</strong>
                        </div>
                        <div className="preview-row">
                          <span>Date & Time:</span>
                          <strong>{bookingForm.date} at {bookingForm.time}</strong>
                        </div>
                        <div className="preview-row">
                          <span>Pickup:</span>
                          <strong>{bookingForm.pickupLocation || 'Not specified'}</strong>
                        </div>
                        <div className="preview-row">
                          <span>Destination:</span>
                          <strong>{bookingForm.destination || 'Not specified'}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="form-navigation">
                  {currentStep > 1 && (
                    <button type="button" className="nav-btn prev-btn" onClick={prevStep}>
                      ← Previous
                    </button>
                  )}
                  
                  {currentStep < 3 ? (
                    <button 
                      type="button" 
                      className="nav-btn next-btn" 
                      onClick={nextStep}
                      disabled={!isStepValid()}
                    >
                      Next →
                    </button>
                  ) : (
                    <button 
                      type="submit" 
                      className="nav-btn submit-btn"
                      disabled={isSubmitting || !bookingForm.vehicleType}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="btn-spinner"></span>
                          Processing...
                        </>
                      ) : (
                        'Confirm Booking'
                      )}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Bookings;